/**
 * Approval Chain Visualizer Component
 * Visual stepper showing L1 -> L2 -> L3 approval progress
 */
import React from 'react';
import { CheckCircle2, Clock, XCircle, ChevronRight } from 'lucide-react';

export default function ApprovalChainVisualizer({ chain = [], currentLevel = 1, compact = false }) {
  // Ensure we have 3 levels
  const levels = [1, 2, 3].map(level => {
    const levelApproval = chain.find(c => c.level === level) || { level, status: 'pending' };
    return {
      level,
      ...levelApproval,
      isCurrent: level === currentLevel,
      isPending: !levelApproval.status || levelApproval.status === 'pending',
      isApproved: levelApproval.status === 'approved',
      isRejected: levelApproval.status === 'rejected'
    };
  });
  
  const levelLabels = {
    1: 'Data Owner',
    2: 'Manager',
    3: 'ESG Head'
  };
  
  if (compact) {
    return (
      <div className="flex items-center gap-1">
        {levels.map((level, idx) => (
          <React.Fragment key={level.level}>
            <div 
              className={`
                w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium
                ${level.isApproved ? 'bg-emerald-500/20 text-emerald-400' : ''}
                ${level.isRejected ? 'bg-red-500/20 text-red-400' : ''}
                ${level.isPending && level.isCurrent ? 'bg-amber-500/20 text-amber-400 ring-2 ring-amber-500/50' : ''}
                ${level.isPending && !level.isCurrent ? 'bg-[#2F2F2F] text-[#6B6B6B]' : ''}
              `}
              title={`Level ${level.level}: ${levelLabels[level.level]} - ${level.isApproved ? 'Approved' : level.isRejected ? 'Rejected' : 'Pending'}`}
            >
              {level.isApproved ? <CheckCircle2 size={12} /> : level.isRejected ? <XCircle size={12} /> : `L${level.level}`}
            </div>
            {idx < 2 && (
              <ChevronRight size={12} className={`${level.isApproved ? 'text-emerald-400/50' : 'text-[#3F3F3F]'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2">
      {levels.map((level, idx) => (
        <React.Fragment key={level.level}>
          <div 
            className={`
              group flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs transition-all cursor-help
              ${level.isApproved ? 'bg-emerald-500/10 border border-emerald-500/30' : ''}
              ${level.isRejected ? 'bg-red-500/10 border border-red-500/30' : ''}
              ${level.isPending && level.isCurrent ? 'bg-amber-500/10 border border-amber-500/30 ring-1 ring-amber-500/50' : ''}
              ${level.isPending && !level.isCurrent ? 'bg-[#1F1F1F] border border-[#2F2F2F]' : ''}
            `}
            title={level.approverName ? `${level.approverName}${level.approvedAt ? ` - ${new Date(level.approvedAt).toLocaleDateString()}` : ''}` : levelLabels[level.level]}
          >
            {level.isApproved ? (
              <CheckCircle2 size={12} className="text-emerald-400" />
            ) : level.isRejected ? (
              <XCircle size={12} className="text-red-400" />
            ) : level.isCurrent ? (
              <Clock size={12} className="text-amber-400 animate-pulse" />
            ) : (
              <div className="w-3 h-3 rounded-full bg-[#3F3F3F]" />
            )}
            <span className={`
              ${level.isApproved ? 'text-emerald-400' : ''}
              ${level.isRejected ? 'text-red-400' : ''}
              ${level.isPending && level.isCurrent ? 'text-amber-400' : ''}
              ${level.isPending && !level.isCurrent ? 'text-[#6B6B6B]' : ''}
            `}>
              L{level.level}
              {level.isApproved && level.approverName && (
                <span className="hidden group-hover:inline text-[10px] ml-1">
                  ({level.approverName.split(' ')[0]})
                </span>
              )}
            </span>
          </div>
          
          {idx < 2 && (
            <ChevronRight 
              size={14} 
              className={`${level.isApproved ? 'text-emerald-400/50' : 'text-[#3F3F3F]'}`} 
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
